'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import * as React from 'react';
import { useStoreEffect } from '@mui/x-internals/store';
import { asCursorAgnosticChatStore } from "../../store/index.js";
import { getMessages, createRuntimeError, getErrorMessage, getMessageIdFromError } from "./useChatControllerHelpers.js";
import { createRealtimeActions } from "./realtimeActions.js";
import { createConversationActions } from "./conversationActions.js";
import { createSendMessageActions } from "./sendMessageActions.js";

// eslint-disable-next-line @typescript-eslint/no-unused-vars

export function useChatController({
  store,
  adapter,
  onToolCall,
  onFinish,
  onData,
  onError,
  streamFlushInterval
}) {
  const runtimeRef = React.useRef({
    adapter,
    onToolCall,
    onFinish,
    onData,
    onError,
    streamFlushInterval
  });
  const assistantMessageIdByUserMessageIdRef = React.useRef(new Map());
  const conversationNavigationRequestIdRef = React.useRef(0);
  const conversationLoadRequestIdRef = React.useRef(0);
  const storeUnknown = asCursorAgnosticChatStore(store);
  runtimeRef.current = {
    adapter,
    onToolCall,
    onFinish,
    onData,
    onError,
    streamFlushInterval
  };
  const setRuntimeError = React.useCallback(error => {
    store.setError(error);
    const messageId = getMessageIdFromError(error);
    if (messageId) {
      store.setMessageError(messageId, error);
    }
    if (error) {
      runtimeRef.current.onError?.(error);
    }
  }, [store]);
  const stopStreaming = React.useCallback(() => {
    store.state.activeStreamAbortController?.abort();
    store.setActiveStreamAbortController(null);
    runtimeRef.current.adapter.stop?.();
  }, [store]);
  const {
    loadConversationMessages,
    loadMoreHistory,
    setActiveConversation
  } = React.useMemo(() => createConversationActions({
    store,
    runtimeRef,
    setRuntimeError,
    stopStreaming,
    conversationNavigationRequestIdRef,
    conversationLoadRequestIdRef
  }), [setRuntimeError, stopStreaming, store]);
  const {
    sendMessage,
    retry,
    pruneAttachmentsByMessageIds
  } = React.useMemo(() => createSendMessageActions({
    store,
    storeUnknown,
    runtimeRef,
    setRuntimeError,
    assistantMessageIdByUserMessageIdRef
  }), [setRuntimeError, store, storeUnknown]);
  const setError = React.useCallback(error => {
    if (error == null) {
      store.setError(null);
      return;
    }
    setRuntimeError(error);
  }, [setRuntimeError, store]);
  const addToolApprovalResponse = React.useCallback(async ({
    id,
    approved,
    reason
  }) => {
    const assistantMessage = getMessages(storeUnknown).find(message => message.role === 'assistant' && message.parts.some(part => (part.type === 'tool' || part.type === 'dynamic-tool') && part.toolInvocation.toolCallId === id));

    // Snapshot the previous parts so we can roll back if the adapter call
    // rejects (#6). Without this, the optimistic mutation that flipped the
    // tool invocation to `approval-responded` would persist even though the
    // adapter never confirmed the response, leaving the UI permanently out
    // of sync with the server.
    const previousParts = assistantMessage?.parts;
    if (assistantMessage) {
      store.updateMessage(assistantMessage.id, {
        parts: assistantMessage.parts.map(part => {
          if (part.type !== 'tool' && part.type !== 'dynamic-tool' || part.toolInvocation.toolCallId !== id) {
            return part;
          }
          return _extends({}, part, {
            toolInvocation: _extends({}, part.toolInvocation, {
              state: 'approval-responded',
              approval: {
                approved,
                reason
              }
            })
          });
        })
      });
    }
    try {
      await runtimeRef.current.adapter.addToolApprovalResponse?.({
        id,
        approved,
        reason
      });
    } catch (error) {
      if (assistantMessage && previousParts) {
        store.updateMessage(assistantMessage.id, {
          parts: previousParts
        });
      }
      setRuntimeError(createRuntimeError('SEND_ERROR', getErrorMessage('Unable to send the tool approval response.', error), 'adapter', true, true, {
        id
      }));
    }
  }, [setRuntimeError, store, storeUnknown]);
  const {
    handleRealtimeEvent
  } = React.useMemo(() => createRealtimeActions({
    store: storeUnknown,
    conversationNavigationRequestIdRef
  }), [storeUnknown, conversationNavigationRequestIdRef]);
  React.useEffect(() => {
    let isDisposed = false;
    if (!adapter.listConversations) {
      return undefined;
    }
    void adapter.listConversations().then(result => {
      if (isDisposed) {
        return;
      }
      store.setConversations(result.conversations);
    }).catch(error => {
      if (isDisposed) {
        return;
      }
      setRuntimeError(createRuntimeError('HISTORY_ERROR', getErrorMessage('Unable to load conversations.', error), 'adapter', true, true));
    });
    return () => {
      isDisposed = true;
    };
  }, [adapter, setRuntimeError, store]);
  React.useEffect(() => {
    let isDisposed = false;
    let cleanup;
    if (!adapter.subscribe) {
      return undefined;
    }
    void Promise.resolve(adapter.subscribe({
      onEvent: handleRealtimeEvent
    })).then(resolvedCleanup => {
      if (isDisposed) {
        resolvedCleanup?.();
        return;
      }
      cleanup = resolvedCleanup ?? undefined;
    }).catch(error => {
      if (isDisposed) {
        return;
      }
      setRuntimeError(createRuntimeError('REALTIME_ERROR', getErrorMessage('Unable to subscribe to chat updates.', error), 'adapter', true, true));
    });
    return () => {
      isDisposed = true;
      cleanup?.();
    };
  }, [adapter, handleRealtimeEvent, setRuntimeError]);
  useStoreEffect(store, state => state.messageIds, (_, nextMessageIds) => {
    pruneAttachmentsByMessageIds(nextMessageIds);
  });
  useStoreEffect(store, state => state.activeConversationId, (_, nextActiveConversationId) => {
    const navId = conversationNavigationRequestIdRef.current;
    if (navId > 0) {
      conversationNavigationRequestIdRef.current = 0;
      return;
    }
    void loadConversationMessages(nextActiveConversationId);
  });
  React.useEffect(() => {
    if (store.state.activeConversationId != null) {
      void loadConversationMessages(store.state.activeConversationId, {
        resetWhenUndefined: false
      });
    }
  }, [loadConversationMessages, store]);
  React.useEffect(() => () => {
    store.state.activeStreamAbortController?.abort();
    store.setActiveStreamAbortController(null);
    runtimeRef.current.adapter.stop?.();
  }, [store]);
  return React.useMemo(() => ({
    sendMessage,
    stopStreaming,
    loadMoreHistory,
    setActiveConversation,
    retry,
    setError,
    addToolApprovalResponse
  }), [addToolApprovalResponse, loadMoreHistory, retry, sendMessage, setActiveConversation, setError, stopStreaming]);
}